import * as React from "react";
import { cn } from "../../lib/utils";
import { DESTINATIONS_SHOWCASE_STYLES } from "./constants";
import type { DestinationsShowcaseProps, IDestinationThumbnail } from "./types";

const NO_LINK_PLACEHOLDER = "#";

function resolveThumbHref(thumb: IDestinationThumbnail): string | undefined {
  const href = thumb.href?.trim();
  return href && href !== NO_LINK_PLACEHOLDER ? href : undefined;
}

export const DestinationsShowcase = React.forwardRef<
  HTMLElement,
  DestinationsShowcaseProps
>(
  (
    { className, title, description, heroImageUrl, thumbnails, openLinksInNewTab = false, ...props },
    ref
  ) => {
    const linkProps = openLinksInNewTab
      ? { target: "_blank" as const, rel: "noopener noreferrer" }
      : {};
    return (
      <section
        ref={ref}
        className={cn(DESTINATIONS_SHOWCASE_STYLES.root, className)}
        {...props}
      >
        <div className={DESTINATIONS_SHOWCASE_STYLES.imageWrapper}>
          <img
            src={heroImageUrl}
            alt={title}
            className={DESTINATIONS_SHOWCASE_STYLES.image}
            loading="lazy"
            decoding="async"
          />
          <div className={DESTINATIONS_SHOWCASE_STYLES.overlay} />

          <div className={DESTINATIONS_SHOWCASE_STYLES.content}>
            <div className={DESTINATIONS_SHOWCASE_STYLES.header}>
              <h2 className={DESTINATIONS_SHOWCASE_STYLES.title}>{title}</h2>
              <p className={DESTINATIONS_SHOWCASE_STYLES.description}>
                {description}
              </p>
            </div>
          </div>
        </div>

        <div className={DESTINATIONS_SHOWCASE_STYLES.grid}>
          {thumbnails.map((thumb) => {
            const href = resolveThumbHref(thumb);
            const content = (
              <>
                <div
                  className={DESTINATIONS_SHOWCASE_STYLES.thumbImageWrapper}
                >
                  <img
                    src={thumb.imageUrl}
                    alt={thumb.name}
                    className={DESTINATIONS_SHOWCASE_STYLES.thumbImage}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <span className={DESTINATIONS_SHOWCASE_STYLES.thumbLocation}>
                  {thumb.location}
                </span>
                <span className={DESTINATIONS_SHOWCASE_STYLES.thumbName}>
                  {thumb.name}
                </span>
              </>
            );

            if (!href) {
              return (
                <div
                  key={thumb.id}
                  className={DESTINATIONS_SHOWCASE_STYLES.thumbLink}
                  data-testid={`destination-thumb-${thumb.id}`}
                >
                  {content}
                </div>
              );
            }

            return (
              <a
                key={thumb.id}
                href={href}
                className={DESTINATIONS_SHOWCASE_STYLES.thumbLink}
                onClick={() => {
                  if (thumb.analyticsData && thumb.analyticsUrl) {
                    try {
                      const payload = btoa(unescape(encodeURIComponent(JSON.stringify(thumb.analyticsData))));
                      navigator.sendBeacon(thumb.analyticsUrl, payload);
                    } catch {
                      // Ignore analytics failures to avoid impacting UX
                    }
                  }
                }}
                {...linkProps}
              >
                {content}
              </a>
            );
          })}
        </div>
      </section>
    );
  }
);

DestinationsShowcase.displayName = "DestinationsShowcase";
